/**
 * Checklist Wizard Component
 *
 * Step-by-step wizard for completing a checklist run.
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router";
import { useWindowId } from "@/components/ui/os-layout/OsWindow";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faCircleNotch,
  faCheck,
  faCircle,
  faExclamationTriangle,
  faPaperPlane,
  faCamera,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { useNotification } from "@/context/NotificationContext";
import { faRobot } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import {
  getRun,
  getChecklist,
  completeStep,
  submitForReview,
  saveStepProgress,
  aiReviewStep,
  uploadScreenshot,
  getStepStatusBgClass,
  type ChecklistRun,
  type Checklist,
  type StepStatus,
  type StepResponse,
  type AIReviewResponse,
  OPS_TEAM_MEMBERS,
} from "@/services/api/opsChecklists";
import { AuthenticatedImage } from "./AuthenticatedImage";
import { ChecklistBulletPoints } from "./ChecklistBulletPoints";
import { ChecklistInstructionsMarkdown } from "./ChecklistInstructionsMarkdown";
import { OpsSubpageHeadingClassic } from "@/components/ops/OpsSubpageHeadingClassic";

function apiErrorDetail(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) return undefined;
  const data = (error as { response?: { data?: { detail?: unknown } } }).response?.data;
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  return undefined;
}

/** When PATCH .../progress is missing (404), we keep WIP answers in sessionStorage for this tab. */
type LocalStepDraftV1 = {
  v: 1;
  draft_rag?: "green" | "amber" | "red";
  notes: string;
  assigned_to: string;
  flagged_confirmed: boolean;
  screenshot_url: string | null;
  updatedAt: number;
};

function localDraftStorageKey(runId: string, stepId: string): string {
  return `ops-checklist-local-draft:v1:${runId}:${stepId}`;
}

function writeLocalStepDraft(
  runId: string,
  stepId: string,
  fields: Omit<LocalStepDraftV1, "v" | "updatedAt">
): void {
  try {
    const payload: LocalStepDraftV1 = {
      v: 1,
      ...fields,
      updatedAt: Date.now(),
    };
    sessionStorage.setItem(localDraftStorageKey(runId, stepId), JSON.stringify(payload));
  } catch (e) {
    console.warn("[ChecklistWizard] local draft save failed:", e);
  }
}

function readLocalStepDraft(runId: string, stepId: string): LocalStepDraftV1 | null {
  try {
    const raw = sessionStorage.getItem(localDraftStorageKey(runId, stepId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalStepDraftV1;
    if (parsed?.v !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearLocalStepDraft(runId: string, stepId: string): void {
  try {
    sessionStorage.removeItem(localDraftStorageKey(runId, stepId));
  } catch {
    /* ignore */
  }
}

function draftRagFromSelection(selectedStatus: StepStatus | null): LocalStepDraftV1["draft_rag"] {
  if (selectedStatus === "green" || selectedStatus === "amber" || selectedStatus === "red") {
    return selectedStatus;
  }
  return undefined;
}

function draftSaveFailureMessage(error: unknown): string {
  const detail = apiErrorDetail(error);
  if (detail) return `Could not save your progress: ${detail}`;
  return "Could not save your progress. Fix any issues and try again.";
}

interface StepIndicatorProps {
  steps: Checklist["steps"];
  responses: StepResponse[];
  currentIndex: number;
  onStepClick: (index: number) => void;
}

function StepIndicator({
  steps,
  responses,
  currentIndex,
  onStepClick,
}: StepIndicatorProps) {
  const hasAnySecondReviewerRejection = responses.some(
    (r) => r.reviewer_step_accepted === false
  );

  return (
    <div>
    <div className="flex items-center gap-1.5 overflow-x-auto overflow-y-visible py-2 px-1 -mx-1">
      {steps.map((step, index) => {
        const response = responses.find((r) => r.step_id === step.step_id);
        const isComplete = !!response && response.status !== "pending";
        const isCurrent = index === currentIndex;
        const hasReviewerComment = !!response?.reviewer_comment;
        const secondReviewerRejected = response?.reviewer_step_accepted === false;

        let bgColor = "bg-slate-300 dark:bg-slate-600";
        if (secondReviewerRejected && response) {
          bgColor = "bg-red-600 dark:bg-red-600";
        } else if (response) {
          if (response.status === "pending" && response.draft_rag) {
            bgColor = getStepStatusBgClass(response.draft_rag);
          } else if (response.status === "pending") {
            bgColor = "bg-slate-300 dark:bg-slate-600";
          } else {
            bgColor = getStepStatusBgClass(response.status);
          }
        } else if (isCurrent) {
          bgColor = "bg-indigo-500";
        }

        const ringClass = isCurrent
          ? secondReviewerRejected
            ? "ring-2 ring-offset-2 ring-red-500 ring-offset-white dark:ring-offset-slate-900"
            : "ring-2 ring-offset-2 ring-indigo-500 ring-offset-white dark:ring-offset-slate-900"
          : secondReviewerRejected
            ? "ring-2 ring-offset-1 ring-red-400/90 ring-offset-white dark:ring-offset-slate-900"
            : "";

        const stepTitle = secondReviewerRejected
          ? `${step.title} — second reviewer rejected this step (update and resave)`
          : step.title;

        return (
          <div key={step.step_id} className="relative shrink-0">
            <button
              type="button"
              onClick={() => onStepClick(index)}
              className={`min-h-9 min-w-9 h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-xs font-medium leading-none tabular-nums transition-all ${bgColor} ${ringClass} cursor-pointer hover:opacity-90 text-white`}
              title={stepTitle}
            >
              {secondReviewerRejected && isComplete ? (
                <FontAwesomeIcon icon={faXmark} className="text-[12px]" aria-hidden />
              ) : secondReviewerRejected ? (
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-[11px]" aria-hidden />
              ) : isComplete ? (
                <FontAwesomeIcon icon={faCheck} className="text-[11px]" aria-hidden />
              ) : (
                index + 1
              )}
            </button>
            {secondReviewerRejected ? (
              <div
                className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 rounded-full border-2 border-white dark:border-slate-900"
                title="Rejected by second reviewer"
              />
            ) : (
              hasReviewerComment && (
                <div
                  className="absolute -top-1 -right-1 w-3 h-3 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900"
                  title="Has reviewer comment"
                />
              )
            )}
          </div>
        );
      })}
    </div>
    {hasAnySecondReviewerRejection && (
      <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-start gap-1.5 leading-snug">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="mt-0.5 shrink-0 text-[11px]"
          aria-hidden
        />
        <span>
          Steps with a red circle and cross were rejected by the second reviewer. Open each one,
          update, and resubmit for review.
        </span>
      </p>
    )}
    </div>
  );
}

interface RAGButtonsProps {
  selected: StepStatus | null;
  onSelect: (status: StepStatus) => void;
}

function RAGButtons({ selected, onSelect }: RAGButtonsProps) {
  return (
    <div className="flex gap-3">
      <button
        onClick={() => onSelect("green")}
        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all border-2 ${
          selected === "green"
            ? "bg-green-500 text-white border-green-600"
            : "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40"
        }`}
      >
        <FontAwesomeIcon icon={faCheck} className="mr-2" />
        Green
        <div className="text-xs font-normal mt-0.5 opacity-75">All good</div>
      </button>
      <button
        onClick={() => onSelect("amber")}
        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all border-2 ${
          selected === "amber"
            ? "bg-amber-500 text-white border-amber-600"
            : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40"
        }`}
      >
        <FontAwesomeIcon icon={faCircle} className="mr-2" />
        Amber
        <div className="text-xs font-normal mt-0.5 opacity-75">Non-blocking</div>
      </button>
      <button
        onClick={() => onSelect("red")}
        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all border-2 ${
          selected === "red"
            ? "bg-red-500 text-white border-red-600"
            : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40"
        }`}
      >
        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
        Red
        <div className="text-xs font-normal mt-0.5 opacity-75">Blocking issue</div>
      </button>
    </div>
  );
}

interface SubmitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (secondChecker: string) => void;
  primaryUser: string;
  isLoading: boolean;
}

function SubmitDialog({
  isOpen,
  onClose,
  onSubmit,
  primaryUser,
  isLoading,
}: SubmitDialogProps) {
  const [selectedReviewer, setSelectedReviewer] = useState<string>("");

  if (!isOpen) return null;

  const availableReviewers = OPS_TEAM_MEMBERS.filter((m) => m !== primaryUser);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Submit for Review
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Select who should review this checklist.
        </p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Second Checker
          </label>
          <select
            value={selectedReviewer}
            onChange={(e) => setSelectedReviewer(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Select reviewer...</option>
            {availableReviewers.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(selectedReviewer)}
            disabled={!selectedReviewer || isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && (
              <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
            )}
            <FontAwesomeIcon icon={faPaperPlane} />
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Summary card for displaying a completed step in read-only mode.
 */
interface StepSummaryCardProps {
  step: Checklist["steps"][0];
  response: StepResponse;
}

function StepSummaryCard({ step, response }: StepSummaryCardProps) {
  const statusBg = getStepStatusBgClass(response.status);
  const statusLabel = response.status.charAt(0).toUpperCase() + response.status.slice(1);

  // Only show AI warning if user ignored a non-green review
  const showAiWarning = response.ai_review_ignored && response.ai_review_rating !== "green";

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${statusBg}`} />
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-slate-900 dark:text-white">
            {step.order}. {step.title}
          </h3>
        </div>
        {/* Only show Timmy warning icon if user ignored a warning */}
        {showAiWarning && (
          <span 
            className={response.ai_review_rating === "red"
              ? "text-red-500 dark:text-red-400"
              : "text-amber-500 dark:text-amber-400"
            }
            title={`Timmy's note: ${response.ai_review_feedback}`}
          >
            <FontAwesomeIcon icon={faRobot} className="text-sm" />
          </span>
        )}
        <span className={`text-xs font-medium px-2 py-1 rounded ${
          response.status === "green" 
            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            : response.status === "amber"
              ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
        }`}>
          {statusLabel}
        </span>
      </div>

      <div className="p-4 bg-slate-50 dark:bg-slate-800/50">
        <div className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
          {response.notes || <em className="text-slate-400">No notes provided</em>}
        </div>

        {/* Screenshot thumbnail */}
        {response.screenshot_url && (
          <div className="mt-3">
            <AuthenticatedImage 
              src={response.screenshot_url}
              alt="Evidence screenshot" 
              className="max-h-24 rounded border border-slate-200 dark:border-slate-700 cursor-pointer hover:opacity-80 transition-opacity"
            />
          </div>
        )}

        {(response.status === "red" || response.status === "amber") && response.assigned_to && (
          <div className={`mt-3 text-xs ${
            response.status === "red" 
              ? "text-red-600 dark:text-red-400" 
              : "text-amber-600 dark:text-amber-400"
          }`}>
            Assigned to {response.assigned_to}
          </div>
        )}
      </div>

      {/* Timmy's feedback - only show if user ignored the warning */}
      {showAiWarning && response.ai_review_feedback && (
        <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-amber-50/50 dark:bg-amber-900/10">
          <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
            <FontAwesomeIcon icon={faRobot} />
            <span>Timmy's note (you continued): {response.ai_review_feedback}</span>
          </div>
        </div>
      )}

      {response.reviewer_comment && (
        <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-indigo-50 dark:bg-indigo-900/20">
          <div className="text-xs font-medium text-indigo-800 dark:text-indigo-300 mb-1">
            Reviewer Comment
          </div>
          <div className="text-sm text-indigo-900 dark:text-indigo-200">
            {response.reviewer_comment}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Read-only summary view for approved checklists.
 */
interface ChecklistSummaryViewProps {
  run: ChecklistRun;
  checklist: Checklist;
  /** Return to the checklists dashboard (classic route or same OS window). */
  onBackToDashboard: () => void;
}

function ChecklistSummaryView({
  run,
  checklist,
  onBackToDashboard,
}: ChecklistSummaryViewProps) {
  const statusCounts = {
    green: run.steps.filter((s) => s.status === "green").length,
    amber: run.steps.filter((s) => s.status === "amber").length,
    red: run.steps.filter((s) => s.status === "red").length,
  };

  return (
    <div className="flex flex-col h-full min-h-0 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0 gap-4">
        <OpsSubpageHeadingClassic
          title={run.checklist_name}
          titleClassName="text-lg font-bold text-slate-900 dark:text-white"
          subtitle={
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {run.date} • {run.primary_user}
            </p>
          }
          onBackClick={onBackToDashboard}
          backButtonAriaLabel="Back to checklists"
          backLinkDataTestId="checklist-summary-back"
          linkClassName="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        />
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-sm font-medium flex-shrink-0">
          <FontAwesomeIcon icon={faCheck} />
          Approved
        </div>
      </div>

      <ChecklistBulletPoints
        items={checklist.bullet_points}
        className="mb-6 flex-shrink-0"
      />

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4 mb-6 flex-shrink-0">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {statusCounts.green}
          </div>
          <div className="text-xs text-green-700 dark:text-green-500">Green</div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            {statusCounts.amber}
          </div>
          <div className="text-xs text-amber-700 dark:text-amber-500">Amber</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {statusCounts.red}
          </div>
          <div className="text-xs text-red-700 dark:text-red-500">Red</div>
        </div>
      </div>

      {/* Completion info */}
      <div className="bg-slate-100 dark:bg-slate-700/50 rounded-lg p-4 mb-6 flex-shrink-0">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-500 dark:text-slate-400">Completed by:</span>
            <span className="ml-2 text-slate-900 dark:text-white font-medium">{run.primary_user}</span>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400">Reviewed by:</span>
            <span className="ml-2 text-slate-900 dark:text-white font-medium">{run.second_checker || "—"}</span>
          </div>
          {run.completed_at && (
            <div>
              <span className="text-slate-500 dark:text-slate-400">Submitted:</span>
              <span className="ml-2 text-slate-900 dark:text-white">{new Date(run.completed_at).toLocaleString()}</span>
            </div>
          )}
          {run.reviewed_at && (
            <div>
              <span className="text-slate-500 dark:text-slate-400">Approved:</span>
              <span className="ml-2 text-slate-900 dark:text-white">{new Date(run.reviewed_at).toLocaleString()}</span>
            </div>
          )}
        </div>
        {run.review_notes && (
          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
            <span className="text-sm text-slate-500 dark:text-slate-400">Approval notes:</span>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">{run.review_notes}</p>
          </div>
        )}
      </div>

      {/* Rejection history (collapsed by default) */}
      {run.rejection_count > 0 && (
        <details className="mb-6 flex-shrink-0">
          <summary className="text-sm text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300">
            Previous rejections ({run.rejection_count})
          </summary>
          <div className="mt-2 bg-slate-100 dark:bg-slate-700/50 rounded-lg p-3 space-y-2">
            {run.rejection_notes.map((note, i) => (
              <div
                key={i}
                className="text-sm text-slate-600 dark:text-slate-400 pl-3 border-l-2 border-slate-300 dark:border-slate-600"
              >
                {note}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Step summaries */}
      <div className="flex-1 min-h-0 overflow-auto space-y-3">
        {checklist.steps.map((step) => {
          const response = run.steps.find((r) => r.step_id === step.step_id);
          if (!response) return null;

          return (
            <StepSummaryCard key={step.step_id} step={step} response={response} />
          );
        })}
      </div>
    </div>
  );
}

export interface ChecklistWizardProps {
  /**
   * When the wizard is shown inside an aniOS window, the run id is passed here
   * (route params are not available).
   */
  windowRunId?: string;
}

export function ChecklistWizard(props: ChecklistWizardProps = {}) {
  const { windowRunId } = props;
  const { runId: runIdParam } = useParams<{ runId: string }>();
  const runId = windowRunId ?? runIdParam;
  const navigate = useNavigate();
  const windowId = useWindowId();
  const osActions = useOsActionsOptional();
  const { addNotification } = useNotification();

  const goToChecklistDashboard = useCallback(() => {
    if (windowId && osActions) {
      osActions.updateWindow(windowId, {
        params: {},
        title: "Ops Checklists",
      });
      return;
    }
    if (windowId && import.meta.env.DEV) {
      console.warn(
        "[ChecklistWizard] windowId is set but OsActions context is missing; falling back to navigate (aniOS window may not update).",
      );
    }
    navigate("/ops/checklists");
  }, [windowId, osActions, navigate]);

  const [run, setRun] = useState<ChecklistRun | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Step form state
  const [selectedStatus, setSelectedStatus] = useState<StepStatus | null>(null);
  const [notes, setNotes] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [flaggedConfirmed, setFlaggedConfirmed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Screenshot state
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  /** True when the user changed the form on this step (vs loaded server data). */
  const [stepFormDirty, setStepFormDirty] = useState(false);

  // Submit dialog
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Timmy AI review state
  const [isReviewing, setIsReviewing] = useState(false);
  const [aiReview, setAiReview] = useState<AIReviewResponse | null>(null);
  const [showAiReview, setShowAiReview] = useState(false);
  /** When true, Complete / Update skips Timmy and finalizes immediately (Timmy review toggle off). */
  const [skipTimmyReview, setSkipTimmyReview] = useState(false);

  // Ref for auto-scroll
  const responseFormRef = React.useRef<HTMLDivElement>(null);

  /** Latest draft saver for debounced auto-save (avoids stale closures). */
  const persistDraftRef = React.useRef<
    (opts?: { silent?: boolean }) => Promise<boolean>
  >(() => Promise.resolve(true));

  /** Prevents a burst of identical draft-save error toasts (non-404 failures). */
  const lastDraftSaveErrorToastAtRef = React.useRef(0);
  /** True after PATCH .../progress returned 404 — draft endpoint missing; use sessionStorage + skip API. */
  const draftProgressUnavailableRef = React.useRef(false);

  const loadData = useCallback(async () => {
    if (!runId) return;

    try {
      const runData = await getRun(runId);
      setRun(runData);

      const checklistData = await getChecklist(runData.checklist_id);
      setChecklist(checklistData);

      // If approved, we'll show summary view - no need to set step index
      if (runData.status === "approved") {
        return;
      }

      // Check if any steps have reviewer comments (indicates rejection/re-entry)
      const hasReviewerComments = runData.steps.some((s) => s.reviewer_comment);
      
      if (hasReviewerComments) {
        // Start at the beginning when re-entering after rejection
        setCurrentStepIndex(0);
      } else {
        // First incomplete step includes drafts (API status pending)
        const completedStepIds = new Set(
          runData.steps.filter((s) => s.status !== "pending").map((s) => s.step_id)
        );
        const firstIncompleteIndex = checklistData.steps.findIndex(
          (s) => !completedStepIds.has(s.step_id)
        );
        setCurrentStepIndex(
          firstIncompleteIndex >= 0 ? firstIncompleteIndex : checklistData.steps.length - 1
        );
      }
    } catch (error) {
      console.error("Failed to load run:", error);
      addNotification({
        type: "error",
        message: "Failed to load checklist run",
      });
    } finally {
      setIsLoading(false);
    }
  }, [runId, addNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load existing response when step changes (server + sessionStorage when draft API is unavailable)
  useEffect(() => {
    if (!checklist || !run || run.status === "approved") return;

    const currentStep = checklist.steps[currentStepIndex];
    const existingResponse = run.steps.find(
      (s) => s.step_id === currentStep.step_id
    );
    const local = readLocalStepDraft(run.run_id, currentStep.step_id);

    if (existingResponse && existingResponse.status !== "pending") {
      clearLocalStepDraft(run.run_id, currentStep.step_id);
      setSelectedStatus(existingResponse.status);
      setNotes(existingResponse.notes);
      setAssignedTo(existingResponse.assigned_to || "");
      setFlaggedConfirmed(existingResponse.flagged_confirmed);
      setScreenshotUrl(existingResponse.screenshot_url || null);
      setScreenshotPreview(null);
    } else if (existingResponse?.status === "pending") {
      const serverTs = new Date(existingResponse.completed_at).getTime();
      const preferLocal =
        local != null && (!Number.isFinite(serverTs) || local.updatedAt >= serverTs);
      if (preferLocal && local) {
        setSelectedStatus(local.draft_rag ?? null);
        setNotes(local.notes);
        setAssignedTo(local.assigned_to || "");
        setFlaggedConfirmed(local.flagged_confirmed);
        setScreenshotUrl(local.screenshot_url || null);
        setScreenshotPreview(null);
      } else {
        setSelectedStatus(existingResponse.draft_rag ?? null);
        setNotes(existingResponse.notes);
        setAssignedTo(existingResponse.assigned_to || "");
        setFlaggedConfirmed(existingResponse.flagged_confirmed);
        setScreenshotUrl(existingResponse.screenshot_url || null);
        setScreenshotPreview(null);
      }
    } else if (local) {
      setSelectedStatus(local.draft_rag ?? null);
      setNotes(local.notes);
      setAssignedTo(local.assigned_to || "");
      setFlaggedConfirmed(local.flagged_confirmed);
      setScreenshotUrl(local.screenshot_url || null);
      setScreenshotPreview(null);
    } else {
      setSelectedStatus(null);
      setNotes("");
      setAssignedTo("");
      setFlaggedConfirmed(false);
      setScreenshotUrl(null);
      setScreenshotPreview(null);
    }
    setStepFormDirty(false);
  }, [currentStepIndex, checklist, run]);

  // Reset Timmy / AI modal state when navigating to a different step or loading a
  // different checklist. Apply checklist default for the Timmy review toggle only then —
  // do not depend on `timmy_review_default` itself or refetches will clear a manual toggle.
  useEffect(() => {
    setAiReview(null);
    setShowAiReview(false);
    if (!checklist) return;
    setSkipTimmyReview(checklist.timmy_review_default === "skip");
  }, [currentStepIndex, checklist?.checklist_id]);

  /**
   * Persist the current step as a draft (pending) without Timmy or finalizing.
   * Skips the API when there is nothing to save or the step is already finalized.
   */
  const persistCurrentStepDraft = useCallback(
    async (options?: { silent?: boolean }): Promise<boolean> => {
      if (!run || !checklist) return true;

      const currentStepDef = checklist.steps[currentStepIndex];
      const er = run.steps.find((s) => s.step_id === currentStepDef.step_id);

      if (er && er.status !== "pending") {
        return true;
      }

      const hasDraftPayload =
        selectedStatus != null ||
        notes.trim().length > 0 ||
        Boolean(screenshotUrl) ||
        Boolean(assignedTo);

      if (!hasDraftPayload) return true;

      const persistLocalOnly = () => {
        writeLocalStepDraft(run.run_id, currentStepDef.step_id, {
          draft_rag: draftRagFromSelection(selectedStatus),
          notes: notes.trim(),
          assigned_to: assignedTo,
          flagged_confirmed: flaggedConfirmed,
          screenshot_url: screenshotUrl,
        });
      };

      if (draftProgressUnavailableRef.current) {
        persistLocalOnly();
        return true;
      }

      try {
        const draftRag = draftRagFromSelection(selectedStatus);
        const updated = await saveStepProgress(run.run_id, currentStepDef.step_id, {
          draft_rag: draftRag,
          notes: notes.trim(),
          assigned_to: assignedTo || undefined,
          flagged_confirmed: flaggedConfirmed,
          screenshot_url: screenshotUrl || undefined,
        });
        clearLocalStepDraft(run.run_id, currentStepDef.step_id);
        setRun(updated);
        setStepFormDirty(false);
        return true;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          draftProgressUnavailableRef.current = true;
          persistLocalOnly();
          return true;
        }
        console.error("Failed to save draft:", error);
        if (!options?.silent) {
          const now = Date.now();
          if (now - lastDraftSaveErrorToastAtRef.current > 5000) {
            lastDraftSaveErrorToastAtRef.current = now;
            addNotification({
              type: "error",
              message: draftSaveFailureMessage(error),
            });
          }
        }
        return false;
      }
    },
    [
      run,
      checklist,
      currentStepIndex,
      selectedStatus,
      notes,
      screenshotUrl,
      assignedTo,
      flaggedConfirmed,
      addNotification,
    ]
  );

  persistDraftRef.current = persistCurrentStepDraft;

  /** Move to the next step after saving any draft (no validation). */
  const handleNextStep = async () => {
    await persistCurrentStepDraft();
    if (!checklist) return;
    if (currentStepIndex < checklist.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  // Debounced auto-save while editing a pending step
  useEffect(() => {
    if (!checklist || !run || run.status === "approved") return;
    if (!stepFormDirty) return;

    const stepDef = checklist.steps[currentStepIndex];
    const er = run.steps.find((s) => s.step_id === stepDef.step_id);
    if (er && er.status !== "pending") return;

    const hasDraftPayload =
      selectedStatus != null ||
      notes.trim().length > 0 ||
      Boolean(screenshotUrl) ||
      Boolean(assignedTo);
    if (!hasDraftPayload) return;

    const t = window.setTimeout(() => {
      void persistDraftRef.current({ silent: true });
    }, 1200);

    return () => window.clearTimeout(t);
  }, [
    stepFormDirty,
    checklist,
    run,
    currentStepIndex,
    selectedStatus,
    notes,
    screenshotUrl,
    assignedTo,
    flaggedConfirmed,
  ]);

  const validateBeforeFinalizeStep = useCallback((): boolean => {
    if (!run || !selectedStatus) return false;
    const needsRemediation = selectedStatus === "amber" || selectedStatus === "red";
    if (needsRemediation) {
      if (!assignedTo) {
        addNotification({
          type: "error",
          message: "Please select who to assign for remediation",
        });
        return false;
      }
      const assigningToSelf = assignedTo === run.primary_user;
      if (!assigningToSelf && !flaggedConfirmed) {
        addNotification({
          type: "error",
          message: "Please confirm you have flagged this to the assignee",
        });
        return false;
      }
    }
    if (!notes.trim()) {
      addNotification({
        type: "error",
        message: "Please add notes",
      });
      return false;
    }
    return true;
  }, [run, selectedStatus, assignedTo, flaggedConfirmed, notes, addNotification]);

  const handleRequestAiReview = async () => {
    if (!run || !checklist || !selectedStatus) return;
    if (!validateBeforeFinalizeStep()) return;

    const currentStep = checklist.steps[currentStepIndex];

    // Request Timmy's review
    setIsReviewing(true);
    try {
      const review = await aiReviewStep({
        step_title: currentStep.title,
        step_instructions: currentStep.instructions,
        user_status: selectedStatus,
        user_notes: notes.trim(),
        screenshot_url: screenshotUrl || undefined,
      });
      setAiReview(review);
      setShowAiReview(true);

      // If green, auto-proceed after a brief moment
      if (review.rating === "green") {
        setTimeout(() => {
          handleSaveStep(false);
        }, 1500);
      }
    } catch (error) {
      console.error("AI review failed:", error);
      // On failure, just proceed without review
      handleSaveStep(false);
    } finally {
      setIsReviewing(false);
    }
  };

  const handleSaveStep = async (
    ignored: boolean = false,
    opts?: { omitAiPayload?: boolean }
  ) => {
    if (!run || !checklist || !selectedStatus) return;

    const currentStep = checklist.steps[currentStepIndex];
    const needsRemediation = selectedStatus === "amber" || selectedStatus === "red";

    setIsSaving(true);
    try {
      const includeAiFields = !opts?.omitAiPayload && aiReview;
      const updatedRun = await completeStep(run.run_id, currentStep.step_id, {
        status: selectedStatus,
        notes: notes.trim(),
        assigned_to: needsRemediation ? assignedTo : undefined,
        flagged_confirmed: needsRemediation ? flaggedConfirmed : false,
        screenshot_url: screenshotUrl || undefined,
        ...(includeAiFields && {
          ai_review_rating: aiReview!.rating,
          ai_review_feedback: aiReview!.feedback,
          ai_review_ignored: ignored,
        }),
      });
      clearLocalStepDraft(run.run_id, currentStep.step_id);
      setRun(updatedRun);
      setStepFormDirty(false);
      setShowAiReview(false);
      setAiReview(null);

      // Move to next step or show submit
      if (currentStepIndex < checklist.steps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } catch (error) {
      console.error("Failed to save step:", error);
      addNotification({
        type: "error",
        message: "Failed to save step",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCompleteSkippingTimmy = async () => {
    if (!validateBeforeFinalizeStep()) return;
    await handleSaveStep(false, { omitAiPayload: true });
  };

  const onPrimaryCompleteClick = skipTimmyReview
    ? handleCompleteSkippingTimmy
    : handleRequestAiReview;
  const primaryCompleteDisabled =
    !selectedStatus || isSaving || (!skipTimmyReview && isReviewing);
  const primaryCompleteBusy =
    isSaving || (!skipTimmyReview && isReviewing);

  const handleEditAfterReview = () => {
    setShowAiReview(false);
    setAiReview(null);
    // Focus back on the notes textarea
  };

  const handleSubmitForReview = async (secondChecker: string) => {
    if (!run) return;

    setIsSubmitting(true);
    try {
      await submitForReview(run.run_id, { second_checker: secondChecker });
      addNotification({
        type: "success",
        message: `Checklist submitted for review by ${secondChecker}`,
      });
      goToChecklistDashboard();
    } catch (error) {
      console.error("Failed to submit for review:", error);
      addNotification({
        type: "error",
        message: "Failed to submit for review",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigateToStep = useCallback(
    async (targetIndex: number) => {
      if (!run || !checklist) return;
      if (targetIndex === currentStepIndex) return;
      if (targetIndex < 0 || targetIndex >= checklist.steps.length) return;

      const currentStepDef = checklist.steps[currentStepIndex];
      const er = run.steps.find((s) => s.step_id === currentStepDef.step_id);

      if (er && er.status !== "pending" && stepFormDirty) {
        addNotification({
          type: "error",
          message:
            "Save this step with Complete step or Update step before switching away.",
        });
        return;
      }

      await persistCurrentStepDraft();
      setCurrentStepIndex(targetIndex);
    },
    [
      run,
      checklist,
      currentStepIndex,
      stepFormDirty,
      persistCurrentStepDraft,
      addNotification,
    ]
  );

  if (isLoading || !run || !checklist) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon
          icon={faCircleNotch}
          className="text-2xl text-indigo-500 animate-spin"
        />
      </div>
    );
  }

  // Show read-only summary view for approved checklists
  if (run.status === "approved") {
    return (
      <ChecklistSummaryView
        run={run}
        checklist={checklist}
        onBackToDashboard={goToChecklistDashboard}
      />
    );
  }

  const currentStep = checklist.steps[currentStepIndex];
  const isLastStep = currentStepIndex === checklist.steps.length - 1;
  const finalizedCount = run.steps.filter((s) => s.status !== "pending").length;
  const allStepsComplete = finalizedCount === checklist.steps.length;
  const hasRedSteps = run.steps.some((s) => s.status === "red");
  const existingResponse = run.steps.find((s) => s.step_id === currentStep.step_id);

  const stepSecondReviewerRejected = existingResponse?.reviewer_step_accepted === false;
  const perStepReviewerNote = existingResponse?.reviewer_comment?.trim() ?? "";
  /** Modal rejection text, or latest entry after `review_notes` was cleared on resume */
  const checklistOrLatestRejectionNote =
    run.review_notes?.trim() ||
    (run.rejection_notes?.length
      ? run.rejection_notes[run.rejection_notes.length - 1]?.trim() ?? ""
      : "");
  const secondReviewerNotesIdentical =
    perStepReviewerNote.length > 0 &&
    perStepReviewerNote === checklistOrLatestRejectionNote;
  const showSecondReviewerFeedbackCard =
    stepSecondReviewerRejected &&
    (perStepReviewerNote.length > 0 || checklistOrLatestRejectionNote.length > 0);

  return (
    <div className="flex flex-col h-full min-h-0 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <OpsSubpageHeadingClassic
          title={run.checklist_name}
          titleClassName="text-lg font-bold text-slate-900 dark:text-white"
          subtitle={
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {run.date} • {run.primary_user}
            </p>
          }
          onBackClick={goToChecklistDashboard}
          backButtonAriaLabel="Back to checklists"
          backLinkDataTestId="checklist-wizard-back"
          linkClassName="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        />
      </div>

      <ChecklistBulletPoints
        items={checklist.bullet_points}
        className="mb-4 flex-shrink-0"
      />

      {/* Rejection banner (if re-entering after rejection) */}
      {run.rejection_count > 0 && run.review_notes && (
        <div className="mb-4 flex-shrink-0 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-red-700 dark:text-red-400 mb-2">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Rejected by reviewer
          </div>
          <div className="text-sm text-red-800 dark:text-red-300 whitespace-pre-wrap">
            {run.review_notes}
          </div>
        </div>
      )}

      {/* Step indicator */}
      <div className="mb-6 flex-shrink-0">
        <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
          Jump to step
        </div>
        <StepIndicator
          steps={checklist.steps}
          responses={run.steps}
          currentIndex={currentStepIndex}
          onStepClick={(index) => {
            void navigateToStep(index);
          }}
        />
      </div>

      {/* Step content */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="max-w-3xl mx-auto">
          {/* Step title */}
          <div className="mb-4">
            <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">
              Step {currentStepIndex + 1} of {checklist.steps.length}
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              {currentStep.title}
            </h2>
            {existingResponse?.reviewer_step_accepted === false && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm text-red-900 dark:border-red-700 dark:bg-red-950/50 dark:text-red-200">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="mt-0.5 shrink-0 text-red-700 dark:text-red-400"
                  aria-hidden
                />
                <span>
                  <span className="font-semibold">Second reviewer rejected this step.</span> Update
                  your answers below, then use &quot;Update Step&quot; or complete the flow and submit
                  for review again.
                </span>
              </div>
            )}
          </div>

          {/* Instructions (Markdown: links, embedded images) */}
          <div className="bg-slate-100 dark:bg-slate-700/50 rounded-xl p-4 mb-6 text-sm">
            <ChecklistInstructionsMarkdown
              content={currentStep.instructions}
              className="!text-slate-700 dark:!text-slate-200"
            />
          </div>

          {/* Second reviewer feedback (per-step + checklist rejection; survives cleared header note via rejection_notes) */}
          {showSecondReviewerFeedbackCard && (
            <div className="border border-red-200 dark:border-red-800 bg-red-50/80 dark:bg-red-950/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-300 mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="shrink-0" aria-hidden />
                Second reviewer feedback
              </div>
              {secondReviewerNotesIdentical ? (
                <div className="text-sm text-red-900 dark:text-red-200 whitespace-pre-wrap">
                  {perStepReviewerNote}
                </div>
              ) : (
                <div className="space-y-4">
                  {perStepReviewerNote.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-red-700/90 dark:text-red-400/90 mb-1">
                        Note on this step
                      </div>
                      <div className="text-sm text-red-900 dark:text-red-200 whitespace-pre-wrap">
                        {perStepReviewerNote}
                      </div>
                    </div>
                  )}
                  {checklistOrLatestRejectionNote.length > 0 && (
                    <div
                      className={
                        perStepReviewerNote.length > 0
                          ? "border-t border-red-200 dark:border-red-800 pt-4"
                          : undefined
                      }
                    >
                      <div className="text-xs font-medium text-red-700/90 dark:text-red-400/90 mb-1">
                        {run.review_notes?.trim()
                          ? "Checklist rejection note"
                          : "Note from last rejection"}
                      </div>
                      <div className="text-sm text-red-900 dark:text-red-200 whitespace-pre-wrap">
                        {checklistOrLatestRejectionNote}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reviewer comment left without a step rejection flag (edge / legacy) */}
          {!stepSecondReviewerRejected && existingResponse?.reviewer_comment && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-2">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                Reviewer comment
              </div>
              <div className="text-sm text-indigo-900 dark:text-indigo-200 whitespace-pre-wrap">
                {existingResponse.reviewer_comment}
              </div>
            </div>
          )}

          {/* RAG selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Status
            </label>
            <RAGButtons
              selected={selectedStatus}
              onSelect={(status) => {
                setStepFormDirty(true);
                setSelectedStatus(status);
                // Auto-scroll to response form after selection
                setTimeout(() => {
                  responseFormRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }, 100);
              }}
            />
          </div>

          {/* Response form - only shows after RAG selection */}
          {selectedStatus && (
            <div ref={responseFormRef} className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
              {/* Amber/Red: Remediation assignment */}
              {(selectedStatus === "amber" || selectedStatus === "red") && (
                <div
                  className={`rounded-xl p-4 space-y-4 ${
                    selectedStatus === "red"
                      ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                      : "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
                  }`}
                >
                  <div
                    className={`text-sm font-medium ${
                      selectedStatus === "red"
                        ? "text-red-700 dark:text-red-400"
                        : "text-amber-700 dark:text-amber-400"
                    }`}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                    {selectedStatus === "red" ? "Blocking Issue" : "Non-blocking Issue"} - Assign for remediation
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Assign to <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={assignedTo}
                      onChange={(e) => {
                        setStepFormDirty(true);
                        setAssignedTo(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">Select person...</option>
                      {OPS_TEAM_MEMBERS.map((name) => (
                        <option key={name} value={name}>
                          {name}{name === run.primary_user ? " (me)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {assignedTo && assignedTo !== run.primary_user && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={flaggedConfirmed}
                        onChange={(e) => {
                          setStepFormDirty(true);
                          setFlaggedConfirmed(e.target.checked);
                        }}
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        I have flagged this to {assignedTo}
                      </span>
                    </label>
                  )}
                </div>
              )}

              {/* Notes — required when completing the step (Timmy / finalize), not for drafts */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Notes{" "}
                  <span className="text-slate-400 font-normal text-xs">
                    (required when you complete this step)
                  </span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => {
                    setStepFormDirty(true);
                    setNotes(e.target.value);
                  }}
                  placeholder={
                    selectedStatus === "green"
                      ? "Confirmed all checks passed. Any observations..."
                      : selectedStatus === "amber"
                        ? "Describe the issue and any context..."
                        : "Describe the blocking issue in detail..."
                  }
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  rows={3}
                  autoFocus
                />
              </div>

              {/* Screenshot upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Screenshot <span className="text-slate-400 text-xs font-normal">(optional)</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    // Show preview immediately
                    const reader = new FileReader();
                    reader.onload = (ev) => setScreenshotPreview(ev.target?.result as string);
                    reader.readAsDataURL(file);
                    
                    // Upload to server
                    setIsUploadingScreenshot(true);
                    try {
                      const { url } = await uploadScreenshot(file);
                      setScreenshotUrl(url);
                      setStepFormDirty(true);
                    } catch (error) {
                      console.error("Screenshot upload failed:", error);
                      addNotification({
                        type: "error",
                        message: "Failed to upload screenshot",
                      });
                      setScreenshotPreview(null);
                    } finally {
                      setIsUploadingScreenshot(false);
                    }
                  }}
                />
                
                {screenshotPreview ? (
                  // New upload - show local preview
                  <div className="relative inline-block">
                    <img 
                      src={screenshotPreview} 
                      alt="Screenshot preview" 
                      className="max-h-32 rounded-lg border border-slate-200 dark:border-slate-700"
                    />
                    {isUploadingScreenshot && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faCircleNotch} className="text-white animate-spin" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setScreenshotUrl(null);
                        setScreenshotPreview(null);
                        setStepFormDirty(true);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <FontAwesomeIcon icon={faXmark} className="text-xs" />
                    </button>
                  </div>
                ) : screenshotUrl ? (
                  // Existing screenshot - fetch with auth
                  <div className="relative inline-block">
                    <AuthenticatedImage 
                      src={screenshotUrl}
                      alt="Screenshot" 
                      className="max-h-32 rounded-lg border border-slate-200 dark:border-slate-700"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setScreenshotUrl(null);
                        setScreenshotPreview(null);
                        setStepFormDirty(true);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <FontAwesomeIcon icon={faXmark} className="text-xs" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-slate-400 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <FontAwesomeIcon icon={faCamera} />
                    Add screenshot
                  </button>
                )}
              </div>

            </div>
          )}
        </div>
      </div>

      {/* Footer navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
        <button
          type="button"
          onClick={() => {
            void navigateToStep(Math.max(0, currentStepIndex - 1));
          }}
          disabled={currentStepIndex === 0}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>

        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* Hide action buttons when AI review is showing (not green) */}
          {!(showAiReview && aiReview?.rating !== "green") && (
            <>
              <label className="group inline-flex cursor-pointer select-none items-center gap-2.5 rounded-xl border border-slate-200/90 bg-white px-3 py-2 shadow-sm transition-all hover:border-slate-300 hover:shadow-md focus-within:ring-2 focus-within:ring-indigo-500/30 focus-within:ring-offset-2 focus-within:ring-offset-white dark:border-slate-600 dark:bg-slate-900/70 dark:hover:border-slate-500 dark:focus-within:ring-offset-slate-950 has-[:checked]:border-indigo-300 has-[:checked]:bg-indigo-50/90 dark:has-[:checked]:border-indigo-500/55 dark:has-[:checked]:bg-indigo-950/50">
                <FontAwesomeIcon
                  icon={faRobot}
                  className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-has-[:checked]:text-indigo-600 dark:group-has-[:checked]:text-indigo-400"
                  aria-hidden
                />
                <span className="text-sm font-medium text-slate-700 transition-colors group-has-[:checked]:text-indigo-950 dark:text-slate-200 dark:group-has-[:checked]:text-indigo-100">
                  Timmy review
                </span>
                <span className="relative ms-0.5 inline-flex h-7 w-11 shrink-0 items-center">
                  <input
                    type="checkbox"
                    checked={!skipTimmyReview}
                    onChange={(e) => setSkipTimmyReview(!e.target.checked)}
                    className="peer sr-only"
                  />
                  <span
                    className="pointer-events-none absolute inset-0 rounded-full bg-slate-200 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white dark:bg-slate-600 dark:peer-focus-visible:ring-offset-slate-950 peer-checked:bg-indigo-600 dark:peer-checked:bg-indigo-500"
                    aria-hidden
                  />
                  <span
                    className="pointer-events-none absolute left-0.5 top-0.5 h-6 w-6 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-200 ease-out will-change-transform peer-checked:translate-x-4 dark:ring-white/10"
                    aria-hidden
                  />
                </span>
              </label>
              {!isLastStep && (
                <>
                  <button
                    type="button"
                    onClick={() => void handleNextStep()}
                    disabled={isSaving || isReviewing}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/25 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {(isSaving || isReviewing) && (
                      <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                    )}
                    Next step
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                  <button
                    type="button"
                    onClick={onPrimaryCompleteClick}
                    disabled={primaryCompleteDisabled}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {primaryCompleteBusy && (
                      <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                    )}
                    <FontAwesomeIcon icon={faCheck} />
                    Complete step
                  </button>
                </>
              )}

              {isLastStep && !allStepsComplete && (
                <button
                  onClick={onPrimaryCompleteClick}
                  disabled={primaryCompleteDisabled}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {primaryCompleteBusy && (
                    <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                  )}
                  <FontAwesomeIcon icon={faCheck} />
                  Complete step
                </button>
              )}

              {allStepsComplete && !hasRedSteps && (
                <>
                  <button
                    onClick={onPrimaryCompleteClick}
                    disabled={primaryCompleteDisabled}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {primaryCompleteBusy && (
                      <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                    )}
                    <FontAwesomeIcon icon={faCheck} />
                    Update Step
                  </button>
                  <button
                    onClick={() => setShowSubmitDialog(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                    Submit for Review
                  </button>
                </>
              )}

              {allStepsComplete && hasRedSteps && (
                <>
                  <button
                    onClick={onPrimaryCompleteClick}
                    disabled={primaryCompleteDisabled}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {primaryCompleteBusy && (
                      <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                    )}
                    <FontAwesomeIcon icon={faCheck} />
                    Update Step
                  </button>
                  <button
                    type="button"
                    onClick={goToChecklistDashboard}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    data-testid="checklist-wizard-save-close"
                  >
                    Save & Close
                  </button>
                  <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    Resolve RED items to submit
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Submit Dialog */}
      <SubmitDialog
        isOpen={showSubmitDialog}
        onClose={() => setShowSubmitDialog(false)}
        onSubmit={handleSubmitForReview}
        primaryUser={run.primary_user || ""}
        isLoading={isSubmitting}
      />

      {/* Timmy Review Modal */}
      {(isReviewing || showAiReview) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            {isReviewing ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <FontAwesomeIcon 
                  icon={faRobot} 
                  className="text-3xl text-slate-400 animate-pulse" 
                />
                <div className="text-slate-600 dark:text-slate-300">
                  Timmy is checking your answer against the instructions...
                </div>
              </div>
            ) : aiReview && (
              <div className="flex flex-col items-center text-center">
                {/* Status indicator */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                  aiReview.rating === "green"
                    ? "bg-green-100 dark:bg-green-900/30"
                    : aiReview.rating === "amber"
                      ? "bg-amber-100 dark:bg-amber-900/30"
                      : "bg-red-100 dark:bg-red-900/30"
                }`}>
                  <FontAwesomeIcon 
                    icon={faRobot} 
                    className={`text-xl ${
                      aiReview.rating === "green"
                        ? "text-green-600"
                        : aiReview.rating === "amber"
                          ? "text-amber-500"
                          : "text-red-500"
                    }`} 
                  />
                </div>
                
                {/* Fun headline */}
                <h3 className={`text-lg font-medium mb-2 ${
                  aiReview.rating === "green"
                    ? "text-green-700 dark:text-green-400"
                    : aiReview.rating === "amber"
                      ? "text-amber-700 dark:text-amber-400"
                      : "text-red-700 dark:text-red-400"
                }`}>
                  {aiReview.rating === "green" 
                    ? "All set — nice work" 
                    : aiReview.rating === "amber"
                      ? "Almost there — a small addition would help"
                      : "Instructions not fully covered yet"}
                </h3>
                
                {/* Feedback */}
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
                  {aiReview.feedback}
                </p>
                
                {/* Actions */}
                <div className="flex justify-center gap-3">
                  {aiReview.rating !== "green" ? (
                    <>
                      <button
                        onClick={handleEditAfterReview}
                        className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        Edit response
                      </button>
                      <button
                        onClick={() => handleSaveStep(true)}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isSaving && <FontAwesomeIcon icon={faCircleNotch} className="animate-spin mr-2" />}
                        Ignore
                      </button>
                    </>
                  ) : (
                    <div className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
                      <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                      Proceeding...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
